/**
 * BIDV Promotional Banner Component
 *
 * Promotional banner for BIDV bank account registration.
 * Used in payment dialog (variant='full') and footer (variant='compact').
 * Shared across thpt, student, and portal apps.
 *
 * Each app must have the logo at public/BIDV-logo-pay2s.svg.
 */

import { useState } from 'react';
import { ExternalLink } from 'lucide-react';

interface BidvPromoBannerProps {
  /** Variant: 'compact' for footer, 'full' for payment dialog */
  variant?: 'compact' | 'full';
  /** Custom className */
  className?: string;
}

const BIDV_FORM_URL =
  'https://docs.google.com/forms/d/e/1FAIpQLSe9rTzHN1Ey0h-npptndXNpVGAf0dkqF80TPnPXCU5cGVYtew/viewform';
const BIDV_LOGO_URL = '/BIDV-logo-pay2s.svg';

export function BidvPromoBanner({ variant = 'full', className = '' }: BidvPromoBannerProps) {
  const [logoError, setLogoError] = useState(false);

  const handleClick = () => {
    window.open(BIDV_FORM_URL, '_blank', 'noopener,noreferrer');
  };

  const handleLogoError = () => {
    setLogoError(true);
  };

  if (variant === 'compact') {
    // Compact version for footer
    return (
      <div
        className={`relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 via-blue-500 to-orange-500 p-4 sm:p-5 ${className}`}
      >
        {/* Decorative background shapes */}
        <div className='absolute inset-0 overflow-hidden'>
          <div className='absolute -top-4 -right-4 w-32 h-32 bg-white/10 rounded-full blur-2xl' />
          <div className='absolute -bottom-4 -left-4 w-24 h-24 bg-white/10 rounded-full blur-xl' />
          <div className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 bg-white/5 rounded-full blur-3xl' />
        </div>

        <div className='relative flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4'>
          {/* BIDV Logo */}
          <div className='flex-shrink-0'>
            <div className='w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-lg flex items-center justify-center shadow-lg p-1 sm:p-1.5'>
              {!logoError ? (
                <img
                  src={BIDV_LOGO_URL}
                  alt='BIDV Logo'
                  className='w-full h-full object-contain'
                  onError={handleLogoError}
                />
              ) : (
                <span className='text-blue-600 font-bold text-lg sm:text-xl'>BIDV</span>
              )}
            </div>
          </div>

          {/* Text Content */}
          <div className='flex-1 min-w-0'>
            <p className='text-white font-semibold text-sm sm:text-base leading-tight'>
              Mở tài khoản BIDV để mở khóa báo cáo đầy đủ miễn phí
            </p>
          </div>

          {/* CTA Button */}
          <button
            onClick={handleClick}
            className='flex-shrink-0 px-4 sm:px-6 py-2 sm:py-2.5 bg-blue-800 hover:bg-blue-900 text-white font-semibold text-xs sm:text-sm rounded-lg transition-all duration-300 hover:scale-105 shadow-lg flex items-center gap-2 whitespace-nowrap'
          >
            <span>Đăng ký ngay</span>
            <ExternalLink className='w-3 h-3 sm:w-4 sm:h-4' />
          </button>
        </div>
      </div>
    );
  }

  // Full version for payment dialog
  return (
    <div
      className={`relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-600 via-blue-500 to-orange-500 p-5 sm:p-6 ${className}`}
    >
      {/* Decorative background shapes */}
      <div className='absolute inset-0 overflow-hidden'>
        <div className='absolute -top-8 -right-8 w-40 h-40 bg-white/10 rounded-full blur-3xl' />
        <div className='absolute -bottom-8 -left-8 w-32 h-32 bg-white/10 rounded-full blur-2xl' />
        <div className='absolute top-1/2 right-1/4 w-48 h-48 bg-white/5 rounded-full blur-3xl' />
      </div>

      <div className='relative flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6'>
        {/* Left: Logo and Text */}
        <div className='flex-1 flex items-start sm:items-center gap-4 min-w-0'>
          {/* BIDV Logo */}
          <div className='flex-shrink-0'>
            <div className='w-20 h-20 sm:w-24 sm:h-24 bg-white rounded-xl flex items-center justify-center shadow-xl p-1.5 sm:p-2'>
              {!logoError ? (
                <img
                  src={BIDV_LOGO_URL}
                  alt='BIDV Logo'
                  className='w-full h-full object-contain'
                  onError={handleLogoError}
                />
              ) : (
                <span className='text-blue-600 font-bold text-xl sm:text-2xl'>BIDV</span>
              )}
            </div>
          </div>

          {/* Text Content */}
          <div className='flex-1 min-w-0'>
            <p className='text-white font-bold text-base sm:text-lg md:text-xl leading-tight'>
              Mở tài khoản BIDV nhận ngay voucher làm bài tư vấn miễn phí
            </p>
          </div>
        </div>

        {/* Right: CTA Button */}
        <button
          onClick={handleClick}
          className='flex-shrink-0 w-full sm:w-auto px-6 sm:px-8 py-3 sm:py-3.5 bg-blue-800 hover:bg-blue-900 text-white font-bold text-sm sm:text-base rounded-lg transition-all duration-300 hover:scale-105 shadow-xl flex items-center justify-center gap-2'
        >
          <span>ĐĂNG KÝ</span>
          <ExternalLink className='w-4 h-4 sm:w-5 sm:h-5' />
        </button>
      </div>

      {/* Illustration hint (optional decorative element) */}
      <div className='absolute bottom-0 right-0 w-24 h-24 sm:w-32 sm:h-32 opacity-10 pointer-events-none'>
        <svg viewBox='0 0 200 200' className='w-full h-full'>
          <circle cx='100' cy='100' r='80' fill='white' />
          <circle cx='80' cy='80' r='20' fill='white' />
          <circle cx='120' cy='80' r='20' fill='white' />
          <path
            d='M 60 120 Q 100 140 140 120'
            stroke='white'
            strokeWidth='8'
            fill='none'
            strokeLinecap='round'
          />
        </svg>
      </div>
    </div>
  );
}

export default BidvPromoBanner;
